import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Container,
  Menu,
  MenuItem,
  Collapse,
  ListItemButton
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import useWidth from '../hooks/useWidth';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Helper for scroll effect
function ElevationScroll({ children, window, setScrolled }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
    target: window ? window() : undefined,
  });

  useEffect(() => {
    setScrolled(trigger);
  }, [trigger, setScrolled]);

  return React.cloneElement(children, {
    style: {
      backgroundColor: trigger ? "#000000" : "transparent", // Black on scroll
      boxShadow: trigger ? "0 0 10px rgba(0,0,0,0.5)" : "none",
      transition: "all 0.4s ease",
      paddingTop: trigger ? "0px" : "15px",
      paddingBottom: trigger ? "0px" : "15px",
    }
  });
}

const AppbarComponent = (props) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [businessAnchor, setBusinessAnchor] = useState(null);
  const [mobileBusinessOpen, setMobileBusinessOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBusinessMenuOpen = (event) => {
    setBusinessAnchor(event.currentTarget);
  };

  const handleBusinessMenuClose = () => {
    setBusinessAnchor(null);
  };

  const navItems = [
    { label: "Who We Are", path: "/who-we-are" },
    { label: "Our Businesses", path: "/#our-businesses" },
    { label: "Contact Us", path: "/contact-us" },
  ];

  const mobileMenu = (
    <Box sx={{ textAlign: 'center', backgroundColor: '#1c1c1c', width: '100%' }}>
      <List>
        {navItems.map((item) => {
          if (item.label === "Our Businesses") {
            return (
              <React.Fragment key={item.label}>
                <ListItem disablePadding onClick={() => setMobileBusinessOpen(!mobileBusinessOpen)}>
                  <Box sx={{ width: '100%', padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: 'white', fontWeight: 600, textTransform: 'uppercase', fontSize: '13px' }}>
                    <ListItemText
                      primary={item.label}
                    />
                  </Box>
                </ListItem>
                <Collapse in={mobileBusinessOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ backgroundColor: '#333' }}>
                    {[
                      { label: "Identity", path: "/business-identity" },
                      { label: "Cloud", path: "/business-cloud" },
                      { label: "Fintech", path: "/business-fintech" },
                      { label: "Authentication", path: "/business-authentication" },
                      { label: "Digital Infrastructure", path: "/business-digital-infrastructure" }
                    ].map((subItem) => (
                      <ListItemButton key={subItem.label} component={Link} to={subItem.path} onClick={handleDrawerToggle} sx={{ pl: 4 }}>
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            style: { color: 'white', fontSize: '12px', textTransform: 'uppercase' }
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItem key={item.label} disablePadding>
              {item.path.startsWith("/#") ? (
                <a href={item.path} style={{ textDecoration: 'none', color: 'white', width: '100%', padding: '15px', fontWeight: 600, textTransform: 'uppercase', fontSize: '13px' }}>
                  <ListItemText primary={item.label} />
                </a>
              ) : (
                <Link to={item.path} style={{ textDecoration: 'none', color: 'white', width: '100%', padding: '15px', fontWeight: 600, textTransform: 'uppercase', fontSize: '13px', display: 'block' }}>
                  <ListItemText primary={item.label} />
                </Link>
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <ElevationScroll {...props} setScrolled={setScrolled}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "white", // Always white text
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: "space-between", minHeight: { xs: "60px", md: "80px" } }}>
            {/* LOGO */}
            <Box
              component={Link}
              to="/"
              sx={{ alignItems: 'center', textDecoration: 'none' }}
            >
              <img
                src={require("../assets/nextix/nextix-logo-white-version.png")}
                alt="next|ix"
                style={{
                  height: scrolled ? "40px" : "60px",
                  objectFit: "contain",
                  transition: "all 0.4s ease"
                  // Always white, no filter needed for black bg
                }}
              />
            </Box>

            {/* DESKTOP NAV */}
            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: 'center' }}>
              {navItems.map((item) => {
                const isHash = item.path.startsWith("/#");
                const isActive = !isHash && location.pathname === item.path;
                const isBusinessDropdown = item.label === "Our Businesses";

                if (isBusinessDropdown) {
                  return (
                    <Box key={item.label}>
                      <Button
                        onClick={handleBusinessMenuOpen}
                        sx={{
                          color: "white",
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: "12px",
                          ml: 4,
                          letterSpacing: "1px",
                          "&:hover": { color: "#82c9ed" },
                          transition: "color 0.3s"
                        }}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={businessAnchor}
                        open={Boolean(businessAnchor)}
                        onClose={handleBusinessMenuClose}
                        PaperProps={{
                          sx: {
                            backgroundColor: "#000",
                            color: "white",
                            mt: 1,
                            minWidth: "200px"
                          }
                        }}
                      >
                        <MenuItem component={Link} to="/business-identity" onClick={handleBusinessMenuClose} sx={{ "&:hover": { backgroundColor: "#1a1a1a" } }}>
                          Identity
                        </MenuItem>
                        <MenuItem component={Link} to="/business-cloud" onClick={handleBusinessMenuClose} sx={{ "&:hover": { backgroundColor: "#1a1a1a" } }}>
                          Cloud
                        </MenuItem>
                        <MenuItem component={Link} to="/business-fintech" onClick={handleBusinessMenuClose} sx={{ "&:hover": { backgroundColor: "#1a1a1a" } }}>
                          Fintech
                        </MenuItem>
                        <MenuItem component={Link} to="/business-authentication" onClick={handleBusinessMenuClose} sx={{ "&:hover": { backgroundColor: "#1a1a1a" } }}>
                          Authentication
                        </MenuItem>
                        <MenuItem component={Link} to="/business-digital-infrastructure" onClick={handleBusinessMenuClose} sx={{ "&:hover": { backgroundColor: "#1a1a1a" } }}>
                          Digital Infrastructure
                        </MenuItem>
                      </Menu>
                    </Box>
                  );
                }

                return isHash ? (
                  <Button
                    key={item.label}
                    href={item.path}
                    className="nav-link"
                    sx={{
                      color: "white",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      fontSize: "12px",
                      ml: 4,
                      letterSpacing: "1px",
                      "&:hover": { color: "#82c9ed" },
                      transition: "color 0.3s"
                    }}
                  >
                    {item.label}
                  </Button>
                ) : (
                  <Button
                    key={item.label}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: isActive ? "#82c9ed" : "white",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      fontSize: "12px",
                      ml: 4,
                      letterSpacing: "1px",
                      "&:hover": { color: "#82c9ed" },
                      transition: "color 0.3s",
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>

            {/* MOBILE MENU ICON */}
            <IconButton
              aria-label="open drawer"
              edge="end"
              alignItems="flex-end"
              justifyContent="flex-end"
              onClick={handleDrawerToggle}
              sx={{
                ml: 1,
                display: { md: "none" },
                color: "white"
              }}
            >
              <MenuIcon />
            </IconButton>

          </Toolbar>
        </Container>

        {/* MOBILE DROPDOWN MENU */}
        <Collapse in={mobileOpen} timeout="auto" unmountOnExit sx={{ display: { md: 'none' }, width: '100%' }}>
          {mobileMenu}
        </Collapse>

      </AppBar>
    </ElevationScroll>
  );
}

export default AppbarComponent;
